import React, { useEffect, useMemo, useRef, useState } from "react";
import "./RetryLogoImage.css";

const LOGO_RETRY_DELAY_MS = 1500;
const LOGO_RETRY_QUERY_KEY = "__chekhabare_logo_retry";
const LOGO_RETRY_SESSION_TOKEN = Date.now().toString(36);

/*
|--------------------------------------------------------------------------
| Shared retry registry
|--------------------------------------------------------------------------
|
| The same logo can be mounted in several places at once. A shared promise
| guarantees that all mounted copies wait for one delayed retry request
| instead of each component creating its own network retry.
|
*/
const sharedLogoRetryRequests = new Map();

const buildRetryUrl = (src) => {
  const value = String(src || "").trim();

  if (!value || /^(data|blob):/i.test(value)) {
    return value;
  }

  const hashIndex = value.indexOf("#");
  const hash = hashIndex >= 0 ? value.slice(hashIndex) : "";
  const path = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const separator = path.includes("?") ? "&" : "?";

  return `${path}${separator}${LOGO_RETRY_QUERY_KEY}=${LOGO_RETRY_SESSION_TOKEN}${hash}`;
};

const requestSharedLogoRetry = (src) => {
  const value = String(src || "").trim();

  if (!value || typeof window === "undefined" || typeof Image === "undefined") {
    return Promise.resolve({ ok: false, url: "" });
  }

  const existingRequest = sharedLogoRetryRequests.get(value);

  if (existingRequest) {
    return existingRequest;
  }

  const retryUrl = buildRetryUrl(value);

  const retryRequest = new Promise((resolve) => {
    window.setTimeout(() => {
      const image = new Image();
      let hasSettled = false;

      const settle = (ok) => {
        if (hasSettled) return;

        hasSettled = true;
        image.onload = null;
        image.onerror = null;
        resolve({ ok, url: retryUrl });
      };

      image.onload = () => {
        settle(image.naturalWidth > 0);
      };

      image.onerror = () => {
        settle(false);
      };

      image.src = retryUrl;

      if (image.complete) {
        settle(image.naturalWidth > 0);
      }
    }, LOGO_RETRY_DELAY_MS);
  });

  sharedLogoRetryRequests.set(value, retryRequest);

  return retryRequest;
};

const RetryLogoImage = ({
  src,
  fallbackSrc = "",
  fallbackText = "",
  className = "",
  alt = "",
  onLoad,
  onFinalError,
  ...imageProps
}) => {
  const primarySrc = String(src || "").trim();
  const secondarySrc = String(fallbackSrc || "").trim();

  const [currentSrc, setCurrentSrc] = useState(primarySrc);
  const [stage, setStage] = useState(primarySrc ? "primary" : "failed");
  const requestSequenceRef = useRef(0);

  const normalizedFallbackText = useMemo(() => {
    return String(fallbackText || "").trim();
  }, [fallbackText]);

  useEffect(() => {
    requestSequenceRef.current += 1;
    setCurrentSrc(primarySrc);
    setStage(primarySrc ? "primary" : "failed");

    return () => {
      requestSequenceRef.current += 1;
    };
  }, [primarySrc, secondarySrc]);

  const finishWithFailure = (event) => {
    setStage("failed");
    setCurrentSrc("");
    onFinalError?.(event);
  };

  const handleError = (event) => {
    if (stage === "primary" && primarySrc) {
      const requestSequence = requestSequenceRef.current;

      setStage("waiting");
      setCurrentSrc("");

      requestSharedLogoRetry(primarySrc).then(({ ok, url }) => {
        if (requestSequenceRef.current !== requestSequence) {
          return;
        }

        if (ok && url) {
          setStage("retry");
          setCurrentSrc(url);
          return;
        }

        if (secondarySrc && secondarySrc !== primarySrc) {
          setStage("fallback");
          setCurrentSrc(secondarySrc);
          return;
        }

        finishWithFailure(event);
      });

      return;
    }

    if (
      stage === "retry" &&
      secondarySrc &&
      secondarySrc !== primarySrc
    ) {
      setStage("fallback");
      setCurrentSrc(secondarySrc);
      return;
    }

    finishWithFailure(event);
  };

  const handleLoad = (event) => {
    const image = event.currentTarget;

    if (!image.complete || image.naturalWidth === 0) {
      handleError(event);
      return;
    }

    onLoad?.(event);
  };

  if (stage === "failed" || !currentSrc) {
    if (!normalizedFallbackText) {
      return null;
    }

    return (
      <span
        aria-hidden="true"
        className={`${className} retry-logo-image__fallback`.trim()}
      >
        {normalizedFallbackText}
      </span>
    );
  }

  return (
    <img
      {...imageProps}
      alt={alt}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      src={currentSrc}
    />
  );
};

export default RetryLogoImage;
export {
  LOGO_RETRY_DELAY_MS,
  buildRetryUrl,
  requestSharedLogoRetry,
};
